import { Cable, Channel, createCable } from "@anycable/web";
import * as Sentry from "@sentry/react";
import { notification } from "antd";
import { useEffect, useState } from "react";
import { baseUrlAltSubdomain } from "../components/Helper/Url";
import { globalAppConfig } from "../config";
import { CalendarEvent } from "../models/CalendarEvent";

interface useAnyCableProps {
    projectInfo: any | null;
    reloadCurrentProject: () => void;
    setNewBroadcastMessage: (value: boolean) => void;
    updateCalendarEventInState: (projectToken: string, calendarEvent: any) => void;
    calendarEventsByProject: { unique_token: string; calendarEvents: any[] }[];
    reloadCalendarEvents: () => void;
}

const useAnyCable = ({
    projectInfo,
    reloadCurrentProject,
    setNewBroadcastMessage,
    updateCalendarEventInState,
    calendarEventsByProject,
    reloadCalendarEvents,
}: useAnyCableProps) => {
    const [cableApp, setCableApp] = useState<Cable | null>(null);
    const [dashboardNotificationsChannel, setDashboardNotificationsChannel] = useState<Channel | null>(null);
    const [mapChannel, setMapChannel] = useState<Channel | null>(null);

    useEffect(() => {
        const appUrl = baseUrlAltSubdomain("app") + "/cable";
        const cableUrl = appUrl.replace("https://", "wss://").replace("http://", "ws://");

        const newCableApp = createCable(cableUrl);

        setCableApp(newCableApp);

        return () => {
            try {
                newCableApp.disconnect();
            } catch (error) {
                Sentry.captureException(error);
            }
        };
        // oxlint-disable-next-line react-hooks/exhaustive-deps This wants cableApp as a dep but that will result in an infinite loop.
    }, []);

    useEffect(() => {
        if (!cableApp || !projectInfo?.unique_token) return;

        const newMapChannel = cableApp?.subscribeTo("MapChannel", {
            project_unique_token: projectInfo.unique_token,
        });

        newMapChannel?.on("message", (data: any) => {
            if (data.method == "update" && data.action == "reload_project") {
                reloadCurrentProject();
            }
        });

        newMapChannel?.on("disconnect", () => {
            setMapChannel(null);
        });

        setMapChannel(newMapChannel || null);

        return () => {
            try {
                newMapChannel?.disconnect();
            } catch (error) {
                Sentry.captureException(error);
            }
        };
    }, [cableApp, reloadCurrentProject, projectInfo?.unique_token]);

    useEffect(() => {
        if (!cableApp) return;

        const newDashboardNotificationsChannel = cableApp?.subscribeTo("DashboardNotificationsChannel");

        newDashboardNotificationsChannel?.on("disconnect", () => {
            setDashboardNotificationsChannel(null);
        });

        setDashboardNotificationsChannel(newDashboardNotificationsChannel || null);

        return () => {
            try {
                newDashboardNotificationsChannel?.disconnect();
            } catch (error) {
                Sentry.captureException(error);
            }
        };
    }, [cableApp]);

    useEffect(() => {
        if (!cableApp || !projectInfo?.unique_token) return;

        const newBroadcastsChannel = cableApp?.subscribeTo("BroadcastsChannel", {
            project_unique_token: projectInfo.unique_token,
        });

        if (newBroadcastsChannel) {
            newBroadcastsChannel?.on("message", (data) => {
                setNewBroadcastMessage(true);
                notification.warning({
                    message: `New Jobsite Alert from ${data.user.fullname}`,
                    description: data.message.body,
                    duration: globalAppConfig.NOTIFICATION_DURATION,
                    placement: "topLeft",
                });
            });
        }

        return () => {
            try {
                newBroadcastsChannel?.disconnect();
            } catch (error) {
                Sentry.captureException(error);
            }
        };
    }, [cableApp, projectInfo?.unique_token, setNewBroadcastMessage]);

    useEffect(() => {
        if (!cableApp || !projectInfo?.unique_token) return;

        const newProcurementManualSyncChannel = cableApp?.subscribeTo("ProcurementManualSyncChannel", {
            project_unique_token: projectInfo.unique_token,
        });

        newProcurementManualSyncChannel?.on("message", (data) => {
            if (data.status == "completed") {
                reloadCurrentProject();
                notification.success({
                    message: `Procurement Sync Completed`,
                    description: `The procurement sync has completed.`,
                    duration: globalAppConfig.NOTIFICATION_DURATION,
                });
            } else if (data.status == "retrying") {
                notification.warning({
                    message: `Procurement Sync Failed`,
                    description: `The procurement sync is being retried in the background.`,
                    duration: globalAppConfig.NOTIFICATION_DURATION,
                });
            }
        });

        return () => {
            try {
                newProcurementManualSyncChannel?.disconnect();
            } catch (error) {
                Sentry.captureException(error);
            }
        };
    }, [cableApp, projectInfo?.unique_token, reloadCurrentProject]);

    useEffect(() => {
        if (!cableApp || !projectInfo || !calendarEventsByProject) {
            return;
        }

        const newCalendarEventsChannel = cableApp.subscribeTo("CalendarEventsChannel", {
            project_unique_token: projectInfo.unique_token,
        });

        newCalendarEventsChannel.on("message", (data: any) => {
            if (data.method == "update" && data.type == "calendar_event") {
                const calendarEvent = CalendarEvent.fromApiObject(data.calendar_event, projectInfo.timezone);

                updateCalendarEventInState(data.project_unique_token, calendarEvent);
            }
        });

        newCalendarEventsChannel.on("message", (data: any) => {
            if (data.method == "refresh_all" && data.type == "calendar_event") {
                reloadCalendarEvents();
            }
        });

        return () => {
            try {
                newCalendarEventsChannel.disconnect();
            } catch (error) {
                Sentry.captureException(error);
            }
        };
    }, [projectInfo, calendarEventsByProject, cableApp, updateCalendarEventInState, reloadCalendarEvents]);

    return {
        cableApp,
        dashboardNotificationsChannel,
        mapChannel,
    };
};

export default useAnyCable;
